import React, { useState } from 'react';
import {
  Typography,
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { useUser } from '../contexts/UserContext';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { getUnreadCount, getNotifications, markAsRead, markAsUnread, markAllAsRead } from '../services/notificationService';
import { formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, logout } = useUser();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);

  const userName = userProfile?.full_name || 'ללא שם'
  const isLoginPage = location.pathname === '/login';
  const isManager = userProfile?.role === 'manager';

  // Fetch unread count (polls every 60s)
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
    enabled: !isLoginPage && isManager,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Fetch recent notifications for popover
  const { data: recentNotifs, isLoading: notifsLoading } = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => getNotifications({ page: 1, pageSize: 7 }),
    enabled: Boolean(notifAnchorEl) && isManager,
    staleTime: 30000,
  });

  const markReadMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markUnreadMutation = useMutation({
    mutationFn: markAsUnread,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unreadCount = unreadData?.count || 0;

  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleNavigateToChangePassword = () => {
    navigate('/change-password');
    handleSettingsClose();
  };

  const handleNavigateToUpdateProfile = () => {
    navigate('/update-profile');
    handleSettingsClose();
  };

  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  const handleNotifClick = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleNotificationClick = (notif) => {
    if (!notif.IsRead) {
      markReadMutation.mutate(notif.NotificationID);
    }
    handleNotifClose();
    if (notif.EntityType === 'client' && notif.ClientBusinessID) {
      navigate(`/client/${notif.ClientBusinessID}`);
    } else if (notif.EntityType === 'supplier' && notif.SupplierID) {
      navigate(`/supplier/${notif.SupplierID}`);
    } else if (notif.EntityType === 'product') {
      navigate('/products');
    }
  };

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate();
  };

  const handleViewAll = () => {
    handleNotifClose();
    navigate('/notifications');
  };

  const handleManageRules = () => {
    handleNotifClose();
    navigate('/notification-rules');
  };

  const notifications = recentNotifs?.results || [];

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        px: { xs: 1, md: 2 },
        py: { xs: 0.75, md: 2 },
        minHeight: { xs: 48, md: 64 },
        backgroundColor: 'primary.main',
        color: 'white',
        position: 'relative',
      }}
    >
      {/* LOGO AND APP NAME */}
      <Box
        display="flex"
        alignItems="center"
        sx={{
          flexShrink: 0,
          cursor: 'pointer',
        }}
        onClick={handleLogoClick}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '6px',
            padding: { xs: '2px 5px', md: '4px 8px' },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <img
            src="/logo.png"
            alt="BalancePro Logo"
            style={{
              height: isMobile ? '20px' : '30px',
              width: 'auto',
            }}
          />
        </Box>
      </Box>

      {/* ICONS */}
      {!isLoginPage && (
        <Box display="flex" alignItems="center" gap={{ xs: 0, md: 0.5 }}>
          {/* User Name and Icon */}
          <Box display="flex" alignItems="center">
            <Typography
              variant="body1"
              sx={{
                color: 'white',
                mr: 0.5,
                fontWeight: 'bold',
                display: { xs: 'none', md: 'block' },
                fontSize: '0.9rem',
              }}
            >
              {userName}
            </Typography>
            <Tooltip title="פרופיל משתמש" placement="bottom">
              <IconButton
                size={isMobile ? 'small' : 'medium'}
                sx={{ color: 'white', p: { xs: 0.5, md: 1 } }}
                onClick={handleNavigateToUpdateProfile}
              >
                <PersonOutlinedIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Notification Bell (Managers Only) */}
          {isManager && (
            <>
              <Tooltip title="התראות" placement="bottom">
                <IconButton
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    color: 'white',
                    p: { xs: 0.5, md: 1 },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                  onClick={handleNotifClick}
                >
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        minWidth: '18px',
                        height: '18px',
                      }
                    }}
                  >
                    <NotificationsOutlinedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Notification Popover */}
              <Popover
                open={Boolean(notifAnchorEl)}
                anchorEl={notifAnchorEl}
                onClose={handleNotifClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'center' : 'right' }}
                sx={{
                  '& .MuiPaper-root': {
                    width: { xs: 'calc(100vw - 32px)', sm: 360 },
                    maxWidth: { xs: 'calc(100vw - 32px)', sm: '100vw' },
                    borderRadius: '12px',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                    overflow: 'hidden',
                    mt: { xs: 0.5, sm: 0 },
                  }
                }}
              >
                {/* Header */}
                <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    התראות
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {unreadCount > 0 && (
                      <Button
                        size="small"
                        onClick={handleMarkAllRead}
                        disabled={markAllReadMutation.isPending}
                        sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                      >
                        סמן הכל כנקרא
                      </Button>
                    )}
                    <Tooltip title="ניהול כללי התראות">
                      <IconButton size="small" onClick={handleManageRules}>
                        <SettingsOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Divider />

                {/* Notification List */}
                {notifsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : notifications.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      אין התראות
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0, maxHeight: 340, overflowY: 'auto' }}>
                    {notifications.map((notif) => (
                      <ListItem key={notif.NotificationID} disablePadding>
                        <ListItemButton
                          onClick={() => handleNotificationClick(notif)}
                          sx={{
                            py: 1.5,
                            px: 2,
                            backgroundColor: notif.IsRead ? 'transparent' : 'rgba(37, 99, 235, 0.04)',
                            '&:hover': {
                              backgroundColor: notif.IsRead ? 'action.hover' : 'rgba(37, 99, 235, 0.08)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%', direction: 'ltr' }}>
                            <Tooltip title={notif.IsRead ? 'סמן כלא נקרא' : 'סמן כנקרא'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (notif.IsRead) {
                                    markUnreadMutation.mutate(notif.NotificationID);
                                  } else {
                                    markReadMutation.mutate(notif.NotificationID);
                                  }
                                }}
                                sx={{ flexShrink: 0, p: 0.3 }}
                              >
                                {notif.IsRead ? (
                                  <DraftsOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                ) : (
                                  <MailOutlineIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, mt: 0.3 }}>
                              {notif.EntityType === 'client' ? (
                                <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              ) : notif.EntityType === 'product' ? (
                                <InventoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              ) : (
                                <LocalShippingIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              )}
                            </Box>
                            <Tooltip title={notif.Title} placement="bottom" enterDelay={400}>
                              <Box sx={{ flex: 1, minWidth: 0, direction: 'rtl', textAlign: 'right' }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: notif.IsRead ? 'normal' : 'bold',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {notif.Title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr', display: 'inline-block' }}>
                                  {notif.EntityType === 'client' ? notif.ClientName : notif.EntityType === 'product' ? notif.ProductName : notif.SupplierName}
                                  {' · '}
                                  {formatDistanceToNow(new Date(notif.CreatedAt), { addSuffix: true, locale: he })}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}

                {/* Footer */}
                <Divider />
                <Box sx={{ p: 1, textAlign: 'center' }}>
                  <Button
                    size="small"
                    onClick={handleViewAll}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    הצג הכל
                  </Button>
                </Box>
              </Popover>
            </>
          )}

          {/* Logout Icon */}
          <Tooltip title="יציאה מהחשבון" placement="bottom">
            <IconButton
              size={isMobile ? 'small' : 'medium'}
              sx={{
                color: 'white',
                p: { xs: 0.5, md: 1 },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)'
                }
              }}
              onClick={logout}
            >
              <LogoutIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Tooltip>

          {/* Settings Icon with Dropdown */}
          <Tooltip title="הגדרות" placement="bottom">
            <IconButton
              size={isMobile ? 'small' : 'medium'}
              sx={{ color: 'white', p: { xs: 0.5, md: 1 } }}
              onClick={handleSettingsClick}
              aria-controls={Boolean(settingsAnchorEl) ? 'settings-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(settingsAnchorEl) ? 'true' : undefined}
            >
              <SettingsOutlinedIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Tooltip>

          {/* Settings Menu */}
          <Menu
            id="settings-menu"
            anchorEl={settingsAnchorEl}
            open={Boolean(settingsAnchorEl)}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                minWidth: 180,
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <MenuItem onClick={handleNavigateToChangePassword}>
              <ListItemIcon>
                <LockOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="שינוי סיסמה" />
            </MenuItem>
            <MenuItem onClick={handleNavigateToUpdateProfile}>
              <ListItemIcon>
                <EditOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="עדכון פרטים אישיים" />
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  );
};

export default Navbar;
